import { Component, ViewContainerRef } from "@angular/core";
import { MatExpansionModule } from "@angular/material/expansion";
import { SvgIconComponent } from "../../svg-icon/svg-icon.component";
import { SharedModule } from "../../../modules/shared/shared.module";
import { SEARCH_BAR_ALIAS_NAME } from "../../list-details-panel/list-details-panel.component";
import { TableMeta } from "../../../models/table-meta.interface";
import { FilterCondition, TableListRequest } from "../../../models/table-list-request.interface";
import { PaginatedListDataResponse } from "../../../models/paginated-list-data-response";
import InvoiceListingTableConfig from "./config/InvoiceListingTableConfig.json";
import { FinanceApiService } from "../../../services/finance-a-p-iervice.service";
import { ActivatedRoute, Router } from "@angular/router";
import { TosTableService } from "../../../services/tos-table.service";
import { LoadingService } from "../../../services/loading.service";
import { InvoiceType, ViewMode } from "../../../constants/finance-constants";
import { ModalComponent } from "../../modal/modal.component";
import { AdvancedFilterFieldInfo } from "../../../models/advanced-filter-config.interface";
import { AdvancedFilterComponentV2, AdvancedFilterOutput } from "../../advanced-filter/advanced-filter-v2.component";
import { CommonUtils } from "../../../utils/common-utils";
import { ToasterService } from "../../../services/toaster.service";
import { IrnGenErrorComponent } from "./irn-gen-error/irn-gen-error.component";
import { CLICKABLE_TD_EVENT_KEY } from "../../tos-table/tos-table-td/tos-table-td.component";

@Component({
  selector: "app-invoice",
  standalone: true,
  imports: [
    MatExpansionModule,
    SvgIconComponent,
    SharedModule
  ],
  templateUrl: "./invoice.component.html",
  styleUrl: "./invoice.component.scss"
})
export class InvoiceComponent {
  searchTerm: string = "";
  focusSearch: boolean =false;
  allTableRecordsSelected: boolean =false;
  tableMeta!: TableMeta;
  menuOptions = [
    { label: "View", value: "view" },
    { label: "Retry IRN", value: "retry-irn", condition: (record: any) => record.status === 'PENDING' },
    { label: "Send Mail", value: "send-mail" },
    { label: "PDF/Print", value: "pdf" },
    { label: "Record Payment", value: "record-payment", condition: (record: any) => record.status !== 'PENDING' },
    { label: "Create Credit Note", value: "credit-note", condition: (record: any) => record.status !== 'PENDING' },
    { label: "Cancel Invoice", value: "cancel-invoice" },
  ];
  tableListData: PaginatedListDataResponse = {
    totalCount: 0,
    page: 0,
    limit: 0,
    records: []
  };
  selectedItems: any[] = [];
  sortColumn: string = "modifiedDate";
  sortDirection: "asc" | "desc" = "desc";
  tableListRequest: TableListRequest = {
    orderBy: {
      [this.sortColumn]: this.sortDirection
    },
    limit: 15,
    page: 0
  };
  private previousTerm: string = "";
  private filterConfig: any[] = InvoiceListingTableConfig.filterConfig;
  private rawFilterData: any[] = [];
  private isFilterApplied: boolean =false;

  constructor(private financeApiService: FinanceApiService,
              private router: Router,
              private route: ActivatedRoute,
              private tosTableService: TosTableService,
              private loadingService: LoadingService,
              private toasterService: ToasterService,
              private viewContainerRef: ViewContainerRef) {
  }

  ngOnInit(){
    this.tableMeta = InvoiceListingTableConfig.listingTableConfig;
    this.getTableList();
  }

  getTableList() {
    this.loadingService.show('inline')
    this.financeApiService.listInvoices(this.tableListRequest).subscribe({
      next: (response: any) => {
        this.tableListData = response.data;
        this.loadingService.hide();
        this.processTableData(this.tableListData);
      },
      error: (error) => {
        this.loadingService.hide();
        this.toasterService.error(error.error.message);
      },
    })
  }

  private processTableData(tableData: any) {
    tableData.records.forEach((record: any) => {
      if (record.irnDetails?.errors?.length > 0) {
        record.irnFailed = true;
      }
    });
  }

  handleOptionSelected(event: any) {
    const { record, actionName } = event;
    switch (actionName) {
      case "record-payment":
        this.router.navigate([`manage-finance/invoices/record-payments/${record.id}`], {
          state: {
            recordPaymentInput: { invoiceIds: [record.id], redirectPath: "/manage-finance/invoices" }
          }
        });
        break;
      case "view":
        this.router.navigate([`manage-finance/invoices/view/${record.id}`], {
          queryParams: {
            type: record.invoiceType === "IMP" ? InvoiceType.DOCUMENT : InvoiceType.MISCELLANEOUS,
            view: ViewMode.LIST
          }
        });
        break;
      case "credit-note":
        this.router.navigate([`manage-finance/credit-notes/add-new`], {
          state: {
            creditNoteInputs: { invoiceId: record.id, redirectPath: "/manage-finance/invoices" }
          }
        });
        break;
      case "retry-irn":
        this.retryIRN(record);
        break;
      default:
        console.warn("Unknown action:", actionName);
    }
  }

  handleRowAction(event: any) {
    const { record, actionName } = event;
    console.log("Row action triggered:", actionName, record);
    switch (actionName) {
      case CLICKABLE_TD_EVENT_KEY:
        this.loadInvoiceCardView(event);
        break;
      case "view-error-log":
        this.openModalForErrorLogs(record);
        break;
      case "generate-irn":
        this.retryIRN(record);
        break;
      default:
        console.warn("Unknown action:", actionName);
    }
  }

  retryIRN(record: any) {
    this.loadingService.show();
    this.financeApiService.generateIRNForInvoice(record.id).subscribe({
      next: (response) => {
        this.loadingService.hide();
        this.toasterService.success("IRN generation in progress. Please verify status later.");
      },
      error: (error) => {
        this.toasterService.error(error.error.errorDesc);
        this.loadingService.hide();
    }});
  }

  addNewDocumentInvoice() {
    this.router.navigate(["add-new"], {
      relativeTo: this.route,
      queryParams: { type: InvoiceType.DOCUMENT, view: ViewMode.LIST}
    });
  }

  addNewMiscellaneousInvoice() {
    this.router.navigate(["add-new"], {
      relativeTo: this.route,
      queryParams: { type: InvoiceType.MISCELLANEOUS, view: ViewMode.LIST }
    });
  }


  handleTableSearch(event: KeyboardEvent) {
    const searchTerm = this.searchTerm.trim();
    if (searchTerm.length > 0) {
      this.tableListRequest.where = {
        filterConditions: [
          {
            name: "SUPER_SEARCH",
            alias: `SUPER_SEARCH_${SEARCH_BAR_ALIAS_NAME}`,
            value: [searchTerm]
          }
        ],
        expression: `SUPER_SEARCH_${SEARCH_BAR_ALIAS_NAME}`
      };
      // Search when Enter is pressed
      if (event.key === "Enter") {
        this.getTableList();
      }
    }
    // Call reset only when the search bar is cleared (avoid on multiple backspaces)
    if (searchTerm.length === 0 && this.previousTerm.length !== 0) {
      delete this.tableListRequest.where;
      this.getTableList();
    }
    this.previousTerm = searchTerm;
  }


  bulkActionConfirmation(active: string) {

  }

  filter(){

  }

  private loadInvoiceCardView(event: any) {
    this.tosTableService.setTosTableConfig({
      selectedItem: event.record.id,
      tableListRequest: this.tableListRequest,
    })
    this.router.navigate(["details"], {
      relativeTo: this.route,
      queryParams: { invoiceId: event.record.id }
    })
  }

  applyFilters() {
    const modalRef = this.viewContainerRef.createComponent(ModalComponent);
    modalRef.instance.title = "Advanced Filter";
    const fieldInfoList: AdvancedFilterFieldInfo[] = CommonUtils.getFilterFieldList(this.filterConfig);

    const filterDialogRef = modalRef.instance.loadComponent(
      AdvancedFilterComponentV2, {
        filterFields: fieldInfoList,
        useNewContract: true,
        appliedFilters: this.rawFilterData
      }
    );

    modalRef.instance.close.subscribe((res: AdvancedFilterOutput) => {
      if (!res) {
        modalRef.destroy();
        return;
      }

      if (res.resetAll) {
        delete this.tableListRequest.where;
        this.rawFilterData = [];
        this.isFilterApplied = false;
        this.getTableList()
      } else if (res.refresh) {
        if (res.filterData?.expression && res.filterData.filterConditions.length > 0) {
          this.rawFilterData = res.rawFilterData;

          const mergedFilterConditions = [
            ...res.filterData.filterConditions,
            ...(this.tableListRequest.where?.filterConditions ?? [])
          ];

          this.tableListRequest.where = {
            ...this.tableListRequest.where,
            filterConditions: mergedFilterConditions,
            expression: this.getExpressionFromSearchBarConfig(mergedFilterConditions)
          };

          this.isFilterApplied = true;
        } else {
          console.error("Invalid filter!");
          delete this.tableListRequest.where;
        }
        this.getTableList()
      }
      modalRef.destroy();
    });
  }

  private getExpressionFromSearchBarConfig(filterConditions: FilterCondition[]) {
    return filterConditions.map((config, index) => {
      return `${config.alias}`;
    }).join(" AND ");
  }


  private openModalForErrorLogs(invoice: any) {
    const modalRef = this.viewContainerRef.createComponent(ModalComponent);
    modalRef.instance.title = "Error Logs";
    const releaseHoldDialogRef = modalRef.instance.loadComponent(IrnGenErrorComponent, {
      invoice: invoice,
      enableRetry: false
    });
    modalRef.instance.close.subscribe((res: any) => {
      modalRef.destroy();
    });
  }

}
